"use client"

import type { CartLine, Client } from "@/hooks/use-pos-cart"

export const HELD_SALES_KEY_PREFIX = "pos_held_sales"
const LEGACY_HOLD_KEY = "pos_hold_cart"

export interface HeldSale {
  id: string
  heldAt: string
  cart: CartLine[]
  selectedClient: Client | null
  salesperson?: string
  salesSource?: string
  headerDiscRs: number
  headerDiscPct: number
  headerDiscMode: "amount" | "percent"
}

export function heldSalesStorageKey(tenantId?: number | null) {
  return tenantId ? `${HELD_SALES_KEY_PREFIX}:${tenantId}` : HELD_SALES_KEY_PREFIX
}

function cartTotal(cart: CartLine[], headerDiscRs: number, headerDiscPct: number, headerDiscMode: "amount" | "percent") {
  const subtotal = cart.reduce((s, l) => s + l.quantity * l.amountRs, 0)
  const lineDisc = cart.reduce((s, l) => s + l.discRs, 0)
  const headerDisc = headerDiscMode === "amount" ? headerDiscRs : Math.max(0, (subtotal - lineDisc) * headerDiscPct / 100)
  return Math.max(0, subtotal - lineDisc - headerDisc)
}

export function heldSaleSummary(held: HeldSale) {
  const qty = held.cart.reduce((s, l) => s + l.quantity, 0)
  const total = cartTotal(held.cart, held.headerDiscRs, held.headerDiscPct, held.headerDiscMode)
  return { qty, total, itemCount: held.cart.length }
}

function normalizeHeld(raw: unknown): HeldSale | null {
  if (!raw || typeof raw !== "object") return null
  const d = raw as Partial<HeldSale> & { cart?: CartLine[] }
  if (!Array.isArray(d.cart) || d.cart.length === 0) return null
  return {
    id: typeof d.id === "string" ? d.id : `hold-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    heldAt: typeof d.heldAt === "string" ? d.heldAt : new Date().toISOString(),
    cart: d.cart,
    selectedClient: d.selectedClient ?? null,
    salesperson: typeof d.salesperson === "string" ? d.salesperson : undefined,
    salesSource: typeof d.salesSource === "string" ? d.salesSource : undefined,
    headerDiscRs: Number(d.headerDiscRs) || 0,
    headerDiscPct: Number(d.headerDiscPct) || 0,
    headerDiscMode: d.headerDiscMode === "percent" ? "percent" : "amount",
  }
}

function loadHeldSalesRaw(tenantId?: number | null): HeldSale[] {
  const key = heldSalesStorageKey(tenantId)
  const raw = localStorage.getItem(key)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map(normalizeHeld).filter((h): h is HeldSale => Boolean(h))
  } catch {
    return []
  }
}

/** Read held sales list; migrates legacy single-hold key if present. */
export function loadHeldSales(tenantId?: number | null): HeldSale[] {
  if (typeof window === "undefined") return []
  try {
    const legacy = localStorage.getItem(LEGACY_HOLD_KEY)
    if (legacy) {
      const migrated = normalizeHeld(JSON.parse(legacy))
      localStorage.removeItem(LEGACY_HOLD_KEY)
      if (migrated) {
        const existing = loadHeldSalesRaw(tenantId)
        const next = [migrated, ...existing]
        localStorage.setItem(heldSalesStorageKey(tenantId), JSON.stringify(next))
        return next
      }
    }
    return loadHeldSalesRaw(tenantId)
  } catch {
    return []
  }
}

export function saveHeldSales(list: HeldSale[], tenantId?: number | null) {
  localStorage.setItem(heldSalesStorageKey(tenantId), JSON.stringify(list))
}

export function addHeldSale(entry: Omit<HeldSale, "id" | "heldAt">, tenantId?: number | null): HeldSale {
  const held: HeldSale = {
    ...entry,
    id: `hold-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
    heldAt: new Date().toISOString(),
  }
  const list = loadHeldSales(tenantId)
  list.unshift(held)
  saveHeldSales(list, tenantId)
  return held
}

export function removeHeldSale(id: string, tenantId?: number | null): HeldSale[] {
  const list = loadHeldSales(tenantId).filter((h) => h.id !== id)
  saveHeldSales(list, tenantId)
  return list
}

export function getHeldSale(id: string, tenantId?: number | null): HeldSale | null {
  return loadHeldSales(tenantId).find((h) => h.id === id) ?? null
}

/** Drop tenant-scoped held carts on logout / tenant switch. */
export function clearHeldSales(tenantId?: number | null) {
  if (typeof window === "undefined") return
  localStorage.removeItem(heldSalesStorageKey(tenantId))
}

export function clearAllHeldSalesForUser(tenantId?: number | null) {
  clearHeldSales(tenantId)
  localStorage.removeItem(LEGACY_HOLD_KEY)
  localStorage.removeItem(HELD_SALES_KEY_PREFIX)
}
